import { Box, Typography, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { AuthStore } from "stores/AuthStore";
import ClientBrainStore from "stores/ClientBrainStore";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import ActiveClassifiersSection from "./sections/ActiveClassifiersSection";
import ClientGoalsSection from "./sections/ClientGoalsSection";
import ClientTypeSection from "./sections/ClientTypeSection";
import HierarchiesSection from "./sections/HierarchiesSection";
import ProgramEventsSection from "./sections/ProgramEventsSection";
import UserProfilesSection from "./sections/UserProfilesSection";
import ClientGlossarySection from "./sections/ClientGlossarySection";

export interface OrganizationClientBrainProps {
    breadcrumbs?: React.ReactNode;
    canEdit?: boolean;
    hideBreadcrumbs?: boolean;
    contentPadding?: number;
    fullWidth?: boolean;
}

const OrganizationClientBrain: React.FC<OrganizationClientBrainProps> =
    observer(
        ({
            breadcrumbs,
            canEdit,
            hideBreadcrumbs,
            contentPadding,
            fullWidth,
        }) => {
        const theme = useTheme();
        const store = useStore(ClientBrainStore);
        const authStore = useStore(AuthStore);
        const messageStore = useStore(MessageStore);
        const orgId = authStore.activeOrgId;

        useEffect(() => {
            if (!orgId) return;
            store.loadAll(orgId, canEdit).catch((err: unknown) => {
                const msg =
                    err instanceof Error
                        ? err.message
                        : "Failed to load Client Brain data";
                messageStore.logError(msg);
            });
            return () => store.resetState();
        }, [store, messageStore, orgId, canEdit]);

        return (
            <Box
                sx={{
                    padding: contentPadding ?? 4,
                    maxWidth: fullWidth
                        ? "none"
                        : `calc(100% - ${theme.spacing(4)})`,
                    width: fullWidth ? "100%" : "1200px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                {!hideBreadcrumbs &&
                    (breadcrumbs ?? (
                        <OrganizationBreadcrumbs
                            pageTitle="Client Brain"
                            sectionLabel="KBo"
                        />
                    ))}

                <Box sx={{ mt: hideBreadcrumbs ? 0 : 2 }}>
                    <Typography
                        variant={hideBreadcrumbs ? "h5" : undefined}
                        fontWeight={600}
                        sx={
                            hideBreadcrumbs
                                ? undefined
                                : {
                                      fontSize: "24px",
                                      color: theme.palette.gray[700],
                                  }
                        }
                    >
                        Client Brain
                    </Typography>
                    <Typography
                        variant={hideBreadcrumbs ? "body2" : undefined}
                        color={hideBreadcrumbs ? "text.secondary" : undefined}
                        sx={{
                            fontSize: hideBreadcrumbs ? undefined : "14px",
                            color: hideBreadcrumbs
                                ? undefined
                                : theme.palette.gray[500],
                            mt: 0.5,
                        }}
                    >
                        Org-level context — KBo uses this to decide what to
                        surface and how to frame findings.
                    </Typography>
                </Box>

                <ClientTypeSection store={store} canEdit={canEdit} />
                <ClientGoalsSection store={store} canEdit={canEdit} />
                <ClientGlossarySection store={store} canEdit={canEdit} />
                <HierarchiesSection
                    store={store}
                    authStore={authStore}
                    canEdit={canEdit}
                />
                <ProgramEventsSection store={store} canEdit={canEdit} />
                <UserProfilesSection store={store} canEdit={canEdit} />
                <ActiveClassifiersSection
                    store={store}
                    authStore={authStore}
                    canEdit={canEdit}
                />
            </Box>
        );
        },
    );

export default OrganizationClientBrain;
